import React, { useMemo, useState } from 'react';
import { useStylesGeneral } from '../../../styles/StyleComponents';
import "../../../styles/site.css";
import TextTitleCS from '../../../common/ComponentesCS/TextTitleCS';
import { Button, Card, CardContent, Grid, Typography, Divider, Chip } from '@material-ui/core';
import AccordionCS from '../../../common/ComponentesCS/AccordionCS';
import MaterialTableCS from '../../../common/ComponentesCS/MaterialTableCS';
import ProductSeriesDialog from '../components/ProductSeriesDialog';

const tableActionButtonStyle = {
    backgroundColor: '#1976d2',
    color: 'white',
    padding: '5px 10px',
    fontSize: '12px',
    textTransform: 'none'
};

const tableCenteredColumnStyle = {
    textAlign: 'center'
};

const tableCenteredActionContentStyle = {
    maxWidth: '220px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    margin: '0 auto'
};

const ProjectOrderPreview = (props) => {
    const {
        projectData,
        invoicesSelected,
        productsSelected,
        productSeries,
        cuentasIntelisis = [],
        conceptosReteFuente = {},
        conceptosReteICA = {},
        title = 'RESUMEN DE ORDEN DE COMPRA'
    } = props;

    const classes = useStylesGeneral();
    const [seriesDialogData, setSeriesDialogData] = useState(null);

    // Función para obtener descripción de cuenta de Intelisis
    const getCuentaDescription = (codigo) => {
        const cuenta = cuentasIntelisis.find(c => c.codigo === codigo);
        return cuenta ? `${cuenta.codigo} - ${cuenta.descripcion}` : codigo;
    };

    // Función para obtener descripción de concepto
    const getConceptoDescription = (codigo, tipoConcepto) => {
        const conceptos = tipoConcepto === 'reteFuente' ? conceptosReteFuente : conceptosReteICA;
        const concepto = Array.isArray(conceptos) ? conceptos.find(c => c.codigo === codigo) : null;
        return concepto ? `${concepto.codigo} - ${concepto.descripcion}` : codigo;
    };

    // Función para obtener series de un producto específico
    const getProductSeries = (idFactura, idArticulo) => {
        const productKey = `${idFactura}-${idArticulo}`;
        return productSeries[productKey] || [];
    };

    // Cálculo de totales por producto y orden completa
    const totalsData = useMemo(() => {
        let grandTotal = 0;
        let totalProductsCount = 0;
        let totalSeriesCount = 0;

        const productTotals = {};

        // Recorrer todos los productos seleccionados
        Object.entries(productsSelected).forEach(([idFactura, products]) => {
            products.forEach(product => {
                const cantidad = parseFloat(product.cantidadSeleccionada) || 0;
                const precioUnitario = parseFloat(product.precioUnitario) || 0;
                const totalProducto = cantidad * precioUnitario;

                // Contar series para este producto
                const productKey = `${idFactura}-${product.idArticulo}`;
                const seriesCount = productSeries[productKey]?.length || 0;

                productTotals[`${idFactura}-${product.idArticulo}`] = {
                    product,
                    idFactura,
                    cantidad,
                    precioUnitario,
                    totalProducto,
                    seriesCount,
                    factura: invoicesSelected.find(inv => inv.idFactura.toString() === idFactura.toString())?.factura
                };

                grandTotal += totalProducto;
                totalProductsCount++;
                totalSeriesCount += seriesCount;
            });
        });

        return {
            productTotals,
            grandTotal,
            totalProductsCount,
            totalSeriesCount,
            totalInvoicesCount: invoicesSelected.length
        };
    }, [productsSelected, productSeries, invoicesSelected]);

    // Configuración de columnas para la tabla resumen de productos con totales
    const productSummaryColumns = [
        {
            field: 'factura',
            title: 'Factura',
            render: (rowData) => `${rowData.idFactura} - ${rowData.factura}`
        },
        {
            field: 'articulo',
            title: 'Artículo',
        },
        {
            field: 'descripcion',
            title: 'Descripción',
        },
        {
            field: 'cantidad',
            title: 'Cant. Total',
            type: 'numeric',
            editable: 'never',
        },
        {
            field: 'cantidadSeleccionada',
            title: 'Cant. Seleccionada',
            type: 'numeric',
            editable: 'never',
        },
        {
            field: 'precioUnitario',
            title: 'Precio Unitario',
            type: 'currency',
            render: (rowData) => (
                <span>
                    ${parseFloat(rowData?.precioUnitario).toFixed(2)}
                </span>
            ),
        },
        {
            field: 'totalProducto',
            title: 'Total Producto',
            type: 'currency',
            render: (rowData) => (
                <span style={{ fontWeight: 'bold', color: '#2e7d32' }}>
                    ${parseFloat(rowData.cantidadSeleccionada * rowData.precioUnitario).toFixed(2)}
                    {/* ${parseFloat(rowData.totalProducto || 0).toFixed(2)} */}
                </span>
            ),
        },
        {
            field: 'series',
            title: 'Series',
            headerStyle: tableCenteredColumnStyle,
            cellStyle: tableCenteredColumnStyle,
            render: (rowData) => {
                const series = getProductSeries(rowData.idFactura, rowData.idArticulo);
                if (series.length === 0) {
                    return <span style={{ color: '#666', fontStyle: 'italic' }}>Sin series</span>;
                }
                return (
                    <div style={tableCenteredActionContentStyle}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => setSeriesDialogData({
                                productData: rowData.product || rowData,
                                series
                            })}
                            style={tableActionButtonStyle}
                        >
                            Ver
                        </Button>
                    </div>
                );
            },
        },
        // {
        //     field: 'intelisisData',
        //     title: 'Configuración Intelisis',
        //     render: (rowData) => {
        //         const product = rowData.product || rowData;

        //         if (product?.altaIntelisis === true) {
        //             return <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>No aplica</span>;
        //         }

        //         const hasIntelisis = product.intelisis_cuenta || product.intelisis_reteFuente || product.intelisis_reteICA;

        //         if (!hasIntelisis) {
        //             return <span style={{ color: '#d32f2f', fontStyle: 'italic' }}>Pendiente configuración</span>;
        //         }

        //         return (
        //             <div style={{ fontSize: '12px' }}>
        //                 {product.intelisis_cuenta && (
        //                     <div><strong>Cuenta:</strong> {product.intelisis_cuenta}</div>
        //                 )}
        //                 {product.intelisis_reteFuente && (
        //                     <div><strong>Retención Fuente:</strong> {product.intelisis_reteFuente}</div>
        //                 )}
        //                 {product.intelisis_reteICA && (
        //                     <div><strong>Retención ICA:</strong> {product.intelisis_reteICA}</div>
        //                 )}
        //             </div>
        //         );
        //     },
        // },
    ];

    // Preparar datos para la tabla de productos con totales calculados
    const productSummaryData = Object.values(totalsData.productTotals).map(item => ({
        ...item.product,
        idFactura: item.idFactura,
        factura: item.factura,
        precioUnitario: item.precioUnitario,
        totalProducto: item.totalProducto,
        seriesCount: item.seriesCount,
        product: item.product
    }));

    return (
        <div className={`${classes.root}`} style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '20px' }}>
            <TextTitleCS title={title} />

            {/* Información General del Proyecto */}
            <Card style={{ marginBottom: '20px' }}>
                <CardContent>
                    <Typography variant="h6" style={{ marginBottom: '15px', fontWeight: 'bold' }}>
                        Información del Proyecto
                    </Typography>
                    <Grid container spacing={2}>
                        {projectData?.cliente && (
                            <Grid item xs={12} md={4}>
                                <Typography variant="body2" color="textSecondary">Cliente</Typography>
                                <Typography variant="body1">
                                    {projectData.cliente}
                                </Typography>
                            </Grid>
                        )}
                        <Grid item xs={12} md={4}>
                            <Typography variant="body2" color="textSecondary">Nombre del Proyecto</Typography>
                            <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                                {projectData?.nombreProyecto || 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="body2" color="textSecondary">ID del Proyecto</Typography>
                            <Typography variant="body1">
                                {projectData?.idProyecto || 'N/A'}
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Resumen Ejecutivo */}
            <Card style={{ marginBottom: '20px' }}>
                <CardContent>
                    <Typography variant="h6" style={{ marginBottom: '15px', fontWeight: 'bold' }}>
                        Resumen Ejecutivo
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={6} md={3}>
                            <Card style={{ padding: '15px', backgroundColor: '#e3f2fd' }}>
                                <Typography variant="h4" style={{ fontWeight: 'bold', color: '#1976d2', textAlign: 'center' }}>
                                    {totalsData.totalInvoicesCount}
                                </Typography>
                                <Typography variant="body2" style={{ textAlign: 'center' }}>
                                    Facturas Seleccionadas
                                </Typography>
                            </Card>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <Card style={{ padding: '15px', backgroundColor: '#e8f5e8' }}>
                                <Typography variant="h4" style={{ fontWeight: 'bold', color: '#2e7d32', textAlign: 'center' }}>
                                    {totalsData.totalProductsCount}
                                </Typography>
                                <Typography variant="body2" style={{ textAlign: 'center' }}>
                                    Productos Configurados
                                </Typography>
                            </Card>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <Card style={{ padding: '15px', backgroundColor: '#fff3e0' }}>
                                <Typography variant="h4" style={{ fontWeight: 'bold', color: '#f57c00', textAlign: 'center' }}>
                                    {totalsData.totalSeriesCount}
                                </Typography>
                                <Typography variant="body2" style={{ textAlign: 'center' }}>
                                    Series Configuradas
                                </Typography>
                            </Card>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <Card style={{ padding: '15px', backgroundColor: '#f3e5f5' }}>
                                <Typography variant="h3" style={{ fontWeight: 'bold', color: '#7b1fa2', textAlign: 'center' }}>
                                    ${totalsData.grandTotal.toFixed(2)}
                                </Typography>
                                <Typography variant="body2" style={{ textAlign: 'center' }}>
                                    Total de la Orden
                                </Typography>
                            </Card>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Facturas Seleccionadas */}
            {/* <AccordionCS
                id="facturas-seleccionadas"
                title="Facturas Seleccionadas"
                descripcion="Listado de facturas incluidas en la orden de compra"
                expanded={false}
            > */}
            <MaterialTableCS
                columns={[
                    { field: 'factura', title: 'Número de Factura' },
                    { field: 'fechaActivo', title: 'Fecha de Activo' },
                    { field: 'cliente', title: 'Cliente' },
                ]}
                data={invoicesSelected}
                title="Facturas en la Orden de Compra"
                pageSize={5}
                showTitle={false}
                selection={false}
                actions={[]}
                pageSizeOptions={[5, 10, 25, 50]}
                toolbar={false}
                search={true}
                paging={true}
                autoHeight={true}
            />
            {/* </AccordionCS> */}

            {/* Detalle de Productos con Totales */}
            {/* <AccordionCS
                id="productos-detalle"
                title="Detalle de Productos y Cálculos"
                descripcion="Productos seleccionados con totales calculados"
                expanded={true}
            >
                <div> */}
            <MaterialTableCS
                columns={productSummaryColumns}
                data={productSummaryData}
                title="Productos Configurados con Totales"
                pageSize={10}
                showTitle={false}
                selection={false}
                actions={[]}
                pageSizeOptions={[5, 10, 25, 50]}
                toolbar={false}
                search={true}
                paging={true}
                autoHeight={true}
            />
            <div style={{ marginTop: '20px', textAlign: 'right', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <Typography variant="h5" style={{ fontWeight: 'bold', color: '#7b1fa2' }}>
                    TOTAL GENERAL: ${totalsData.grandTotal.toFixed(2)}
                </Typography>
            </div>
            {/* </div>
            </AccordionCS> */}
            <ProductSeriesDialog
                open={Boolean(seriesDialogData)}
                onClose={() => setSeriesDialogData(null)}
                onSave={async () => false}
                productData={seriesDialogData?.productData || null}
                existingSeries={seriesDialogData?.series || []}
                readOnly
            />
        </div>
    );
};

export default ProjectOrderPreview;
